import { useClient, useMutation } from 'Data/Api.js'
import setup from './setup.js'
import queryProviderLocationAccount from './query-provider-location-account.graphql.js'
import mutationAddTokenGraphql from './mutation-add-token.graphql.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import {
  isBankAccountMetadataValid,
  isCreditCardMetadataValid,
} from './helpers.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let onActionInitialize = useDataOnActionInitialize(props, data)
  let onActionRender = useDataOnActionRender(props, data)
  let onActionTokenize = useDataOnActionTokenize(props, data)

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args?.type) {
      case 'initialize': {
        return await onActionInitialize({ value, originalValue, args, change })
      }
      case 'render': {
        return await onActionRender({ value, originalValue, args, change })
      }
      case 'tokenize': {
        return await onActionTokenize({ value, originalValue, args, change })
      }
      default: {
        throw new Error(`Unkwnown payment action ${args.type}`)
      }
    }
  }
}

function useDataOnActionInitialize(props, data) {
  let client = useClient()

  return async function onAction({ value, originalValue, args, change }) {
    if (!args.locationId) return

    try {
      if (value.state !== 'loading') {
        change(next => {
          next.state = 'loading'
          next.containerId = `${next.containerId.split('--')[0]}--${Date.now()}`
          next.render = null
          next.submit = null
          next.provider = null
          next.provider_location_account_id = null
          next.person_id = null
          next.reusable = false
          next.valid = false
        })
      }
      let response = await client
        .query(queryProviderLocationAccount, { location_id: args.locationId })
        .toPromise()
      if (response.error) {
        let error = new Error('PaymentProvider/query-provider-location-account')
        error.meta = response.error
        throw error
      }

      let [providerLocationAccount] =
        response.data.payments_provider_location_accounts
      if (!providerLocationAccount) {
        let error = new Error(
          'PaymentProvider/missing-provider-location-account'
        )
        error.meta = response.error
        throw error
      }

      let render = await setup({
        provider: providerLocationAccount.provider,
      })

      change(next => {
        next.state = 'ready'
        next.render = render
        next.provider = providerLocationAccount.provider
        next.provider_account_id = providerLocationAccount.account_id
        next.provider_location_account_id = providerLocationAccount.id
        next.person_id = args.person._id
        next.person = args.person
        next.valid = false
        next.reusable =
          typeof args.reusable === 'boolean' ? args.reusable : false
        next.method = args.method
        next.token_id = null
        next.add_new = true
      })
    } catch (error) {
      console.error({
        type: 'onActionInitialize/setup',
        message: error.message,
        stack: error.stack,
        meta: error.meta,
        location_id: args.locationId,
      })
      change(next => {
        next.state = 'error'
        next.render = null
        next.submit = null
        next.provider_location_account_id = null
        next.person_id = null
        next.reusable = false
        next.valid = false
      })
    }
  }
}

function useDataOnActionRender(props, data) {
  return async function onAction({ value, originalValue, args, change }) {
    if (typeof value.render !== 'function') return

    try {
      let submit = value.render({
        accountId: value.provider_account_id,
        containerId: value.containerId,
        method: value.method,
        person: value.person,
        onValid: valid => change(valid, 'valid'),
      })
      change(next => {
        next.submit = submit
        next.render = null
      })
    } catch (error) {
      console.error({
        type: 'onActionInitialize/setup',
        message: error.message,
        stack: error.stack,
        meta: error.meta,
        location_id: args.locationId,
      })
      change(next => {
        next.state = 'error'
        next.render = null
        next.submit = null
        next.provider_location_account_id = null
        next.person_id = null
        next.reusable = false
        next.valid = false
        next.method = null
      })
    }
  }
}

function useDataOnActionTokenize(props, data) {
  let [, executeMutation] = useMutation(mutationAddTokenGraphql)
  let [, notify] = useNotifications()

  return async function onAction({ value, originalValue, args, change }) {
    if (!value.add_new && value.token_id) return value.token_id

    try {
      let first_name = null
      let last_name = null
      let zip = null
      if (value.provider === 'bluefin') {
        if (value.method === 'card') {
          if (!isCreditCardMetadataValid(value.credit_card)) {
            notify(
              notifyError(
                'The credit card details provided are missing or invalid.'
              )
            )
            return true
          }
          zip = value.credit_card.zip_code
          first_name = value.credit_card.holder_first_name
          last_name = value.credit_card.holder_last_name
        } else {
          if (!isBankAccountMetadataValid(value.bank_account)) {
            notify(
              notifyError(
                'The bank account details provided are missing or invalid.'
              )
            )
            return true
          }
          first_name = value.credit_card.holder_first_name
          last_name = value.credit_card.holder_last_name
        }
      }
      let response = await value.submit()
      if (zip !== null) {
        response.zip = zip
      }
      if (first_name !== null && last_name !== null) {
        response.first_name = first_name
        response.last_name = last_name
      }

      let mutationResponse = await executeMutation({
        type: value.method,
        reusable: value.reusable,
        person_id: value.person_id,
        provider_location_account_id: value.provider_location_account_id,
        response,
      })
      if (mutationResponse.error) throw mutationResponse.error

      change(next => {
        next.add_new = false
        next.token_id = mutationResponse.data.payments_add_token.token_id
        next.credit_card = {
          holder_first_name: '',
          holder_last_name: '',
          zip_code: '',
        }
        next.bank_account = {
          holder_first_name: '',
          holder_last_name: '',
        }
      })

      return mutationResponse.data.payments_add_token.token_id
    } catch (error) {
      console.error({ type: 'PaymentProvider/tokenize', error })
      return null
      // TODO: review adding a reset mechanism to allow reusing the widget
      // if a submission fails. Alternatively, we can keep the token in memory
      // if that part worked and it was us that failed and show a different
      // kind of input here?
      // } finally {
      //   change(next => {
      //     next.containerId = `${next.containerId}-${Date.now()}`
      //   })
    }
  }
}
